import { Injectable, Inject } from "@angular/core";
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from "@angular/router";
import { MsalService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from "@azure/msal-angular";
import { RedirectRequest } from "@azure/msal-browser";
import { firstValueFrom } from "rxjs";
import { UserService } from "app/core/user/user.service";

export const PM_DEEP_LINK_KEY = "pending_pm_deeplink_after_login";

// auth.guard.ts
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  constructor(
    private msalService: MsalService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private router: Router,
    private readonly userService: UserService,
  ) { }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    try {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        if (!this.msalService.instance.getActiveAccount()) {
          this.msalService.instance.setActiveAccount(accounts[0]);
        }

        const hasPortalRole = await this.ensurePortalRole();
        if (!hasPortalRole) {
          this.router.navigate(["/no-access"]);
          return false;
        }

        // Check if there is a pending PM deep-link from before login.
        const pendingUrl = this.consumePendingPmKey(state.url);
        if (pendingUrl) {
          console.error('[PM-DEEPLINK] AuthGuard: redirecting to pending PM URL:', pendingUrl);
          return this.router.parseUrl(pendingUrl);
        }

        return true;
      }

      const configAuthRequest = this.msalGuardConfig.authRequest;
      const authRequest = typeof configAuthRequest === 'function'
        ? configAuthRequest(this.msalService, state)
        : configAuthRequest || {};

      // Persist the PM deep-link so it can be restored after login.
      const normalizedPmUrl = this.normalizePmUrl(state.url);
      if (normalizedPmUrl) {
        localStorage.setItem(PM_DEEP_LINK_KEY, normalizedPmUrl);
        sessionStorage.setItem(PM_DEEP_LINK_KEY, normalizedPmUrl);
        console.error('[PM-DEEPLINK] AuthGuard: saved key before loginRedirect:', normalizedPmUrl);
      }

      const request: RedirectRequest = {
        authority: authRequest.authority,
        scopes: authRequest.scopes || [],
        redirectStartPage: normalizedPmUrl ?? state.url,
      };

      this.msalService.loginRedirect(request);
      return false;
    } catch (error) {
      console.error('AuthGuard Error:', error);
      this.router.navigate(['/sign-in']);
      return false;
    }
  }

  private consumePendingPmKey(currentUrl: string): string | null {
    const stored = localStorage.getItem(PM_DEEP_LINK_KEY) || sessionStorage.getItem(PM_DEEP_LINK_KEY);
    if (!stored) { return null; }
    localStorage.removeItem(PM_DEEP_LINK_KEY);
    sessionStorage.removeItem(PM_DEEP_LINK_KEY);
    const normalized = this.normalizePmUrl(stored);
    if (!normalized) { return null; }
    // Already on the target PM URL — no redirect needed.
    if (this.normalizePmUrl(currentUrl) === normalized) { return null; }
    return normalized;
  }

  normalizePmUrl(url: string): string | null {
    try {
      const parsed = new URL(url, window.location.origin);
      const path = parsed.pathname.toLowerCase();
      if (path !== '/monitoring/performance-monitor' && path !== '/fokus/performance-monitor') {
        return null;
      }
      return '/monitoring/performance-monitor' + parsed.search;
    } catch {
      return null;
    }
  }

  private async ensurePortalRole(): Promise<boolean> {
    const activeAccount = this.msalService.instance.getActiveAccount();
    const activeUsername = String(activeAccount?.username ?? "").trim().toLowerCase();
    const cachedUserRaw = localStorage.getItem("loggedInUser");
    if (cachedUserRaw) {
      try {
        const cachedUser = JSON.parse(cachedUserRaw);
        const cachedEmail = String(cachedUser?.email ?? "").trim().toLowerCase();
        const cachedRole = String(cachedUser?.portalRole ?? "").trim();
        if (cachedRole && (!activeUsername || !cachedEmail || cachedEmail === activeUsername)) {
          return true;
        }
      } catch {
        // Ignore parse error and fall back to API.
      }
    }

    try {
      const user = await firstValueFrom(this.userService.get());
      return !!String(user?.portalRole ?? "").trim();
    } catch {
      return false;
    }
  }
}
